import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import LivingSite from '../components/LivingSite';
import { TOWERS, IMAGE_W, IMAGE_H } from '../data/livingSiteData';

const TOWER_BY_N = Object.fromEntries(TOWERS.map(t => [t.n, t]));
// the centre of a building as a share of the picture (the point the map zooms to)
const towerSpot = n => { const t = TOWER_BY_N[n]; return t ? [(t.x + t.w / 2) / IMAGE_W * 100, (t.y + t.h / 2) / IMAGE_H * 100] : [50, 50]; };
const ZOOM_MS = 650;

export default function Placements() {
  const { projectName } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const building = parseInt(params.get('b'), 10) || null;
  const findName = params.get('find') || '';

  const frameRef = useRef(null);
  const [summary, setSummary] = useState([]);
  const [html, setHtml] = useState('');
  const [error, setError] = useState('');
  const [askLeave, setAskLeave] = useState(false);
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom] = useState(null);      // the building the map is zooming to
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);       // where the searched tenant is placed
  const [shown, setShown] = useState(null);     // the result whose building is highlighted on the map

  const planApi = () => { try { return frameRef.current && frameRef.current.contentWindow.__placements; } catch { return null; } };
  const toProject = () => navigate(`/project-hub/${projectName}`);
  const toPicker = () => { setHtml(''); setAskLeave(false); setParams({}); };

  useEffect(() => {
    if (building) return;
    api.get('/portal/placements-summary').then(r => setSummary(r.data || [])).catch(() => setError('לא הצלחתי לטעון את רשימת הבניינים'));
  }, [building]);

  useEffect(() => { if (!building) setZoom(null); }, [building]);

  const openBuilding = (n, find) => {
    const next = find ? { b: String(n), find } : { b: String(n) };
    if (!TOWER_BY_N[n]) { setParams(next); return; }
    setZoom(n);
    setTimeout(() => setParams(next), ZOOM_MS);
  };

  // search a tenant by name: the buildings where they are placed light up on the map
  useEffect(() => {
    const text = q.trim();
    setShown(null);
    if (text.length < 2) { setFound([]); return undefined; }
    const t = setTimeout(() => {
      api.get('/portal/placements-search', { params: { q: text } }).then(r => setFound(r.data || [])).catch(() => setFound([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const foundBuildings = new Set(shown ? [shown.n] : found.filter(x => x.n).map(x => x.n));
  const unitsLabel = Object.fromEntries(summary.map(x => [x.n, (x.assigned + (x.public || 0)) + '/' + x.apts]));

  useEffect(() => {
    if (!building) return;
    setHtml(''); setError('');
    api.get(`/portal/placements-page/building/${building}`, { params: findName ? { find: findName } : {}, responseType: 'text', transformResponse: r => r })
      .then(r => setHtml(r.data))
      .catch(() => setError('לא הצלחתי לטעון את מסך השיבוצים'));
  }, [building, findName]);

  useEffect(() => {
    const warn = e => { const p = planApi(); if (p && p.isDirty()) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  function goBack() {
    if (!building) { toProject(); return; }
    const p = planApi();
    if (p && p.isDirty()) setAskLeave(true); else toPicker();
  }

  async function saveAndLeave() {
    const p = planApi();
    setSaving(true);
    const ok = p ? await p.save() : false;
    setSaving(false);
    if (ok) toPicker(); else alert('השמירה נכשלה, השינויים לא נשמרו. נסי שוב.');
  }

  const btn = { border: 'none', borderRadius: 8, padding: '10px 16px', cursor: 'pointer', fontWeight: 700, fontSize: '0.95rem' };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', direction: 'rtl', background: '#f3f4f6' }}>
      <style>{'@keyframes mapPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(37,99,235,0.7); } 50% { box-shadow: 0 0 0 14px rgba(37,99,235,0); } }'}</style>
      <div style={{ background: '#1B2A4A', color: 'white', padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ fontWeight: 700 }}>🏢 שיבוצים{building ? ` — בניין ${building}` : ''}</div>
        <button className="btn btn-secondary" onClick={goBack}>{building ? '← לכל הבניינים' : '← חזרה'}</button>
      </div>

      {error && <div style={{ padding: 20, color: '#b91c1c' }}>{error}</div>}

      {!building && !error && (
        <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: '1.15rem', color: '#1B2A4A', marginBottom: 6 }}>בחרו בניין</div>
          <div style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: 10 }}>לחצו על בניין בתמונה כדי להתקרב אליו ולראות את הדירות שלו, או חפשו דייר לפי שם</div>
          <div style={{ position: 'relative', marginBottom: 12, maxWidth: 460 }}>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 חיפוש דייר לפי שם, למשל: לוי אור" aria-label="חיפוש דייר"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: 10, border: '1px solid #cbd5e1', fontSize: '1rem', fontFamily: 'inherit' }} />
            {q.trim().length >= 2 && (
              <div style={{ position: 'absolute', zIndex: 5, top: '100%', right: 0, left: 0, marginTop: 4, background: 'white', borderRadius: 10, boxShadow: '0 6px 24px rgba(0,0,0,0.2)', maxHeight: 260, overflowY: 'auto' }}>
                {found.length === 0 && <div style={{ padding: 12, color: '#6b7280' }}>לא נמצא דייר בשם הזה</div>}
                {found.map((x, i) => x.n === null ? (
                  <div key={i} style={{ padding: '9px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.9rem', color: '#64748b' }}>
                    <b style={{ color: '#334155' }}>{x.name}</b> · לא שובץ (דירה {x.dn} ב{x.st})
                  </div>
                ) : (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, borderBottom: '1px solid #f1f5f9', background: shown === x ? '#eff6ff' : 'white' }}>
                    <button onClick={() => setShown(x)} style={{ flex: 1, textAlign: 'right', background: 'none', border: 'none', padding: '9px 12px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.9rem' }}>
                      <b>{x.name}</b> <span style={{ color: '#475569' }}>· בניין {x.n} · קומה {x.f}{x.area ? ' · ' + x.area + ' מ״ר' : ''}</span>
                    </button>
                    <button onClick={() => openBuilding(x.n, x.name)} style={{ flexShrink: 0, margin: '0 8px', padding: '4px 10px', borderRadius: 8, border: '1px solid #2563eb', background: 'white', color: '#2563eb', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.8rem' }}>פתיחה ←</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ overflowX: 'auto', borderRadius: 14 }}>
            <div style={{ position: 'relative', minWidth: 720, borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.15)', background: '#0e1a2b' }}>
              <div style={{ position: 'relative', transition: `transform ${ZOOM_MS}ms ease-in-out`, transformOrigin: zoom ? towerSpot(zoom).map(v => v + '%').join(' ') : '50% 50%', transform: zoom ? 'scale(3.4)' : 'scale(1)' }}>
                <LivingSite units={unitsLabel} found={[...foundBuildings]} selected={zoom || 0} onSelect={n => openBuilding(n)} title="המתחם בתנועה" />
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', margin: '10px 0 4px', fontSize: '0.8rem', color: '#374151' }}>
            <span>המספר בתוך הריבוע: דירות ששובצו (דיירים + דיור ציבורי) / כל הדירות בבניין</span>
          </div>
          {summary.length === 0 && <div style={{ color: '#6b7280' }}>טוען...</div>}
        </div>
      )}

      {building && !error && !html && <div style={{ padding: 20, color: '#6b7280' }}>טוען...</div>}
      {building && html && <iframe ref={frameRef} key={building} title={`שיבוצים בניין ${building}`} srcDoc={html} style={{ flex: 1, width: '100%', border: 'none' }} />}

      {askLeave && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 14, padding: 22, width: '100%', maxWidth: 400 }}>
            <div style={{ fontWeight: 800, fontSize: '1.1rem', marginBottom: 8 }}>בוצעו שינויים בשיבוצים</div>
            <div style={{ color: '#374151', marginBottom: 18 }}>האם לשמור את השינויים לפני היציאה?</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button disabled={saving} onClick={saveAndLeave} style={{ ...btn, background: '#15803d', color: 'white', flex: 1 }}>{saving ? 'שומר...' : 'כן, שמור'}</button>
              <button disabled={saving} onClick={toPicker} style={{ ...btn, background: '#fee2e2', color: '#b91c1c', flex: 1 }}>לא, אל תשמור</button>
              <button disabled={saving} onClick={() => setAskLeave(false)} style={{ ...btn, background: '#f3f4f6', color: '#374151', flex: '1 1 100%' }}>חזרה לעריכה</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
